import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function OwnerDashboard() {
  const [stats, setStats] = useState(null);
  const [obits, setObits] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("obituaries");
  const [resetForm, setResetForm] = useState({ email: "", new_password: "" });
  const [resetBusy, setResetBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/obituaries").then(({ data }) => setObits(data.items || []));
    api.get("/admin/users").then(({ data }) => setUsers(data.items || []));
  }, []);

  const downloadCsv = (path, filename) => {
    const token = localStorage.getItem("or_token");
    const url = `${API}${path}?token=${encodeURIComponent(token || "")}`;
    // Admin CSV endpoints require Authorization header — but since this is a same-window
    // anchor click, we fall back to fetch + blob to attach the bearer token cleanly.
    api.get(path, { responseType: "blob" }).then((res) => {
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(`Downloaded ${filename}.`);
    }).catch(() => toast.error("Download failed."));
  };

  const resetUserPw = async (e) => {
    e.preventDefault();
    setResetBusy(true);
    try {
      await api.post("/admin/reset-user-password", resetForm);
      toast.success(`Password reset for ${resetForm.email}.`);
      setResetForm({ email: "", new_password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed.");
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: "30px auto", padding: "0 24px" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 6 }}>OWNER DASHBOARD</div>
        <h1 className="serif" style={{ fontSize: 38, color: "#fff", margin: 0, fontWeight: 400 }}>All Obituaries</h1>
      </div>

      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 24 }}>
          {[
            { l: "Total Users", v: stats.total_users },
            { l: "Active Subscribers", v: stats.active_subscribers },
            { l: "Total Obituaries", v: stats.total_obituaries },
            { l: "Paid Transactions", v: stats.paid_transactions },
          ].map((s, i) => (
            <div key={i} className="bg-card-gold" style={{ padding: 20 }}>
              <div className="label-dark">{s.l}</div>
              <div className="serif" style={{ fontSize: 36, color: "var(--gold)", marginTop: 6 }}>{s.v}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <button data-testid="admin-tab-obits" onClick={() => setTab("obituaries")} className={tab === "obituaries" ? "btn-gold" : "btn-ghost"}>Obituaries</button>
        <button data-testid="admin-tab-users" onClick={() => setTab("users")} className={tab === "users" ? "btn-gold" : "btn-ghost"}>Users</button>
        <button data-testid="admin-tab-tools" onClick={() => setTab("tools")} className={tab === "tools" ? "btn-gold" : "btn-ghost"}>Tools</button>
        <div style={{ flex: 1 }} />
        {tab === "obituaries" && (
          <button data-testid="admin-export-obits-csv" onClick={() => downloadCsv("/admin/obituaries.csv", "obituaries.csv")} className="btn-ghost" style={{ fontSize: 12 }}>
            ⇩ Export CSV
          </button>
        )}
        {tab === "users" && (
          <button data-testid="admin-export-users-csv" onClick={() => downloadCsv("/admin/users.csv", "users.csv")} className="btn-ghost" style={{ fontSize: 12 }}>
            ⇩ Export CSV
          </button>
        )}
      </div>

      {tab === "obituaries" && (
        <div className="bg-card" style={{ padding: 0, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--bg-3)" }}>
                <th style={th}>Author</th><th style={th}>Deceased</th><th style={th}>Created</th><th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {obits.map(o => (
                <tr key={o.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={td}>{o.user_email}</td>
                  <td style={td}>{o.deceased_name}</td>
                  <td style={td}>{new Date(o.created_at).toLocaleDateString()}</td>
                  <td style={td}><Link to={`/obituary/${o.id}`} className="link-gold">View</Link></td>
                </tr>
              ))}
              {obits.length === 0 && <tr><td colSpan={4} style={{ ...td, textAlign: "center", color: "var(--text-muted)" }}>No obituaries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "users" && (
        <div className="bg-card" style={{ padding: 0, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--bg-3)" }}>
                <th style={th}>Email</th><th style={th}>Name</th><th style={th}>Status</th><th style={th}>Created</th><th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={td}>{u.email}</td>
                  <td style={td}>{u.name}</td>
                  <td style={td}><span style={{ padding: "3px 10px", borderRadius: 999, background: u.subscription_status === "active" ? "rgba(212,175,55,0.18)" : "var(--bg-3)", color: u.subscription_status === "active" ? "var(--gold)" : "var(--text-muted)", fontSize: 11 }}>{u.subscription_status || "incomplete"}</span></td>
                  <td style={td}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td style={td}><button data-testid={`reset-pw-${u.email}`} onClick={() => { setResetForm({ email: u.email, new_password: "" }); setTab("tools"); }} className="link-gold" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12 }}>Reset password</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "tools" && (
        <div className="bg-card" style={{ padding: 28, maxWidth: 540 }}>
          <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 6 }}>Reset a user&apos;s password</div>
          <p className="muted" style={{ fontSize: 13, marginBottom: 18 }}>
            Use this when a customer can&apos;t receive their reset code. Type the user&apos;s email and a temporary password — then text or call them with the new password.
          </p>
          <form onSubmit={resetUserPw} style={{ display: "grid", gap: 10 }}>
            <input data-testid="admin-reset-email" required type="email" placeholder="user@email.com" value={resetForm.email} onChange={(e) => setResetForm({ ...resetForm, email: e.target.value })} className="input-dark" />
            <input data-testid="admin-reset-new" required type="text" minLength={6} placeholder="New password (give this to the user)" value={resetForm.new_password} onChange={(e) => setResetForm({ ...resetForm, new_password: e.target.value })} className="input-dark" />
            <button data-testid="admin-reset-submit" type="submit" disabled={resetBusy} className="btn-gold" style={{ marginTop: 4 }}>{resetBusy ? "Resetting…" : "Reset password"}</button>
          </form>
        </div>
      )}
    </div>
  );
}
const th = { padding: "12px 16px", textAlign: "left", color: "var(--gold)", fontWeight: 600, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" };
const td = { padding: "12px 16px", color: "var(--text)" };
